import React from 'react';
import classNames from 'classnames';
import {Button, Stack, Subheading, ButtonGroup} from '@shopify/polaris';
import {WithI18nProps, withI18n} from '@shopify/react-i18n';
import {KeyCodes, EVENTS} from 'global-constants';
import {FilterMajorMonotone} from '@shopify/polaris-icons';
import i18nArgs from 'sections/services-marketplace/experts/locales';

import styles from './FiltersContainer.scss';

interface OwnProps {
  children: React.ReactNode;
  buttonText?: string;
  showDesktopActions?: boolean;
  onSubmit?(): void;
  onReset?(): void;
}

type CombinedProps = OwnProps & WithI18nProps;

interface State {
  filtersExpanded: boolean;
}

class FiltersContainer extends React.PureComponent<CombinedProps, State> {
  state: State = {
    filtersExpanded: false,
  };

  onSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event && event.preventDefault();
    this.props.onSubmit && this.props.onSubmit();
    this.closeFilters();
  };

  openFilters = () => {
    this.toggleScrollLock(true);
    this.setState({
      filtersExpanded: true,
    });
  };

  closeFilters = () => {
    this.toggleScrollLock(false);
    this.setState({
      filtersExpanded: false,
    });
  };

  toggleScrollLock = (lock: boolean) => {
    document.body.classList.toggle(styles.FiltersLockScroll, lock);
  };

  onKeyupEvent: EventListener = (evt: Event) => {
    if ((evt as KeyboardEvent).keyCode !== KeyCodes.esc) return;

    this.setState({
      filtersExpanded: false,
    });
  };

  componentDidMount() {
    document.addEventListener(EVENTS.keyup, this.onKeyupEvent);
  }

  componentWillUnmount() {
    this.toggleScrollLock(false);
    document.removeEventListener(EVENTS.keyup, this.onKeyupEvent);
  }

  render() {
    const {
      children,
      buttonText,
      showDesktopActions,
      onReset,
      i18n,
    } = this.props;
    const {filtersExpanded} = this.state;
    const className = classNames(
      styles.Filters,
      filtersExpanded && styles.FiltersExpanded
    );

    const ResetButton = (
      <Button onClick={onReset} fullWidth plain>
        {i18n.translate('Global.Filters.resetButtonText')}
      </Button>
    );

    const SubmitButton = (
      <Button
        primary
        fullWidth
        onClick={this.onSubmit}
        ariaControls={styles.Filters}
        ariaExpanded={filtersExpanded}
      >
        {buttonText
          ? buttonText
          : i18n.translate('Global.Filters.saveButtonText')}
      </Button>
    );

    const DesktopActions = (
      <div className={styles.DesktopActions}>
        <ButtonGroup>
          {SubmitButton}
          {onReset && ResetButton}
        </ButtonGroup>
      </div>
    );

    const MobileActions = (
      <div className={styles.MobileActions}>
        <Stack alignment="center">
          <Stack.Item fill>{SubmitButton}</Stack.Item>
          {onReset && <Stack.Item fill>{ResetButton}</Stack.Item>}
        </Stack>
      </div>
    );

    return (
      <div className={styles.FiltersContainer}>
        <form onSubmit={this.onSubmit}>
          <div className={styles.ToggleFiltersButton}>
            <Button
              onClick={this.openFilters}
              ariaControls={styles.Filters}
              ariaExpanded={filtersExpanded}
              icon={FilterMajorMonotone}
            >
              {i18n.translate('Global.Filters.toggleButtonText')}
            </Button>
          </div>
          <div
            className={className}
            id={styles.Filters}
            aria-hidden={!filtersExpanded}
          >
            <div className={styles.FiltersHeader}>
              <Subheading>
                {i18n.translate('Global.Filters.headerText')}
              </Subheading>
              <Button
                onClick={this.closeFilters}
                icon="cancel"
                ariaControls={styles.Filters}
                ariaExpanded={filtersExpanded}
                accessibilityLabel={i18n.translate(
                  'Global.Filters.closeButton'
                )}
                plain
              />
            </div>
            <div className={styles.Children}>
              {children}
              {showDesktopActions && DesktopActions}
            </div>
            {MobileActions}
          </div>
          <button
            type="submit"
            aria-hidden="true"
            className={styles.SubmitButton}
          >
            {i18n.translate('Global.Filters.saveButtonText')}
          </button>
        </form>
      </div>
    );
  }
}

export default withI18n(i18nArgs)(FiltersContainer);
